import { Injectable, Output, EventEmitter } from "@angular/core";
import { Observable, forkJoin, of } from "rxjs";
import { Router, NavigationStart } from "@angular/router";
import { AuthService, AuthType } from "./auth.service";
import { Addon } from "src/app/shared/models/addon.model";
import { map, switchMap, mergeMap, tap, catchError, shareReplay } from "rxjs/operators";
import { environment } from "src/environments/environment";
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpRequest,
} from "@angular/common/http";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { authentication } from "@microsoft/teams-js";
import { throwError } from "rxjs/internal/observable/throwError";
import { IStemexeModule } from "src/app/data-providers/interfaces/interfaces";
import { StemexeDataProviderService } from "src/app/data-providers/services/stemexe-data-provider.service";
import { DataProvider, PagedData, PagedDataItem } from "src/app/data-providers/models/models";
import { ActivitiesService } from "src/app/activities/activity/services/activities.service";
import { EspDataProviderService } from "src/app/requests/esp/common/services/esp-data-provider.service";
import { PlannexeDataProviderService } from "src/app/plannexe/stemexe-providers.service";
import { EngagementProDataProviderService } from "src/app/engagement/engagement.pro.providers.service";
import { OpporunityProDataProviderService } from "src/app/opporunity/opportunity.stemexe.providers.service";
import { TechnadoptDataProviderService } from "src/app/products/stemexe-providers.service";

const IDENEDI_TOKEN_STORAGE_KEY = "idenedi.auth.token";
@Injectable({
  providedIn: "root",
})
export class SimplestrataAuthService {
  public loggedInUserId: any;
  public engagementProLoggedInUserId: any = null;
  public engagementProTeams: any = null;
  public engagementProData: any = null;
  public oppProData: any = null;
  isEngagementProEnabled: boolean = false;
  idenediToken: any;
  
  private SimpleStrataToken$: Observable<Addon> | null = null;
  private readonly ssLoggedInUserIdKey = 'ss.loggedInUserId';

  constructor(private _authService: AuthService, private http: HttpClient, private router: Router) {
    this.restoreLoggedInUserId();
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.SimpleStrataToken$ = null;
      }
    });
  }

  getSimpleStrataAuth(): Observable<Addon> {
    return this._authService
      .getAddonsAuth(this._authService.idenediGroupId)
      .pipe(
        map((addons) => {
          return addons.find(
            (addon) =>
              addon.Type.toLowerCase() == AuthType.SimpleStrata.toLowerCase()
          );
        })
      );
  }

  getIdenediCode(): Observable<string> {
    let url = `${environment.idenediProviderUrl}/api/oauth/AuthorizationCode??client_id=XSS1977020300`;
    if (this._authService.idenediGroupId) {
      url = `${environment.idenediProviderUrl}/api/oauth/AuthorizationCode?client_id=XSS1977020300&gIdenedi=${this._authService.idenediGroupId}`;
    }
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.Idenedi),
    };
    return this.http.get<any>(url, options).pipe(
      map((code) => {
        return code;
      })
    );
  }

  getSimpleStrataToken(): Observable<Addon> {
    if (this.SimpleStrataToken$) {
      return this.SimpleStrataToken$;
    }
    this.SimpleStrataToken$ = this.getSimpleStrataAuth().pipe(
      switchMap((addon) => {
        if(!!addon){
          if (addon.AccessToken) {
            localStorage.setItem("SignInByIdenediToken", addon.AccessToken);
            this.restoreLoggedInUserId();
          }
          // Cached addon tokens skip SignInByIdenedi, which is the only place
          // loggedInUserId is assigned. If we already know the user, keep the
          // fast path; otherwise SignIn so AssignedBy_UserId is not empty (#46404).
          if (addon.AccessToken && this.getResolvedUserId()) {
            return this.getSimpleStrataAuth();
          } else {
            return forkJoin([
              this.getSimpleStrataAuth(),
              this.getIdenediCode(),
            ]).pipe(
              switchMap((results) => {
                // results[0] is our getSimpleStrataAuth() - addon
                // results[1] is our getIdenediCode() - code
                const headers = new HttpHeaders({
                  "client-id": "web",
                  locale: "en",
                });
                let orgId = localStorage.getItem("organziationId");
                let data: {
                  IdenediCode: string;
                  OrganizationId?: any;
                  IdenediGroupId?: string;
                } = {
                  IdenediCode: results[1],
                  OrganizationId: orgId,
                  IdenediGroupId: this._authService.idenediGroupId
                };
  
                if (environment.production) {
                  data.IdenediGroupId = this._authService.idenediGroupId;
                  data.OrganizationId = results[0].OrganziationId;
                }
                let url1 = results[0].BaseUrl;
                if (url1.includes("http://")) {
                  url1 = url1.replace("http://", "https://");
                }
                return this.http
                  .post<any>(
                    `${url1}api/UserApi/SignInByIdenedi`,
                    data,
                    { headers: headers }
                  )
                  .pipe(
                    map((resp) => {
                      this.persistLoggedInUserId(resp && resp.ResponseResult ? resp.ResponseResult.userId : null);
                      results[0].AccessToken = resp && resp.ResponseResult ? resp.ResponseResult.access_token : null;
                      if (resp && resp.ResponseResult) {
                        localStorage.setItem("SignInByIdenediToken", resp.ResponseResult.access_token);
                      }
                      return results[0];
                    })
                  );
              })
            );
          }
        } else {
          return of(null);
        }
      }),
      shareReplay(1),
      catchError(err => {
        // VERY IMPORTANT
        this.SimpleStrataToken$ = null;
        return throwError(() => err);
      })
    );

    return this.SimpleStrataToken$;
  }

  persistLoggedInUserId(userId: any): void {
    if (userId == null || userId === '') {
      return;
    }
    this.loggedInUserId = userId;
    localStorage.setItem(this.ssLoggedInUserIdKey, String(userId));
  }

  restoreLoggedInUserId(): void {
    if (this.loggedInUserId != null && this.loggedInUserId !== '') {
      return;
    }
    const stored = localStorage.getItem(this.ssLoggedInUserIdKey);
    if (stored) {
      this.loggedInUserId = stored;
    }
  }

  getResolvedUserId(): number | null {
    this.restoreLoggedInUserId();
    const n = Number(this.loggedInUserId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
}

@Injectable({
  providedIn: "root",
})
export class SimpleStrataTokenResolverService
   {
  constructor(
    private _simplestrataAuthService: SimplestrataAuthService,
    private _authService: AuthService) { }

  resolve(route: ActivatedRouteSnapshot = null): Observable<any> {
    if (route?.data?.ignoreToken && !this._authService.isLoggedIn()) {
      return of(true);
    } else {
      return this._simplestrataAuthService
        .getSimpleStrataToken()
        .pipe(map((simpleStrataAddon) => {
          localStorage.removeItem("secondaryIds");
          if (simpleStrataAddon && simpleStrataAddon.PublicProperties && simpleStrataAddon.PublicProperties.secondaryIds) {
            localStorage.setItem("secondaryIds", simpleStrataAddon.PublicProperties.secondaryIds)
          }
          simpleStrataAddon
        }));
    }
  }
}

@Injectable({
  providedIn: "root",
})
export class engagementProTokenResolverService
   {
  constructor(
    private _authService: AuthService,
    private http: HttpClient,
    private _simplestrataAuthService: SimplestrataAuthService,
    private _engproDataProvider: EngagementProDataProviderService
  ) {
    this._engproDataProvider.initialize();
  }

  resolve() {
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.Stemexe),
    };

    let url = environment.production
      ? `https://stemexess2.azurewebsites.net/api/Stemexe/AuthenticateUser`
      : `https://stemexess.azurewebsites.net/api/Stemexe/AuthenticateUser`;
    let data = {
      module: "EngagementPro",
      clientCode: environment.engProClientCode,
      userInfo: {
        uniqueKey: localStorage.getItem(IDENEDI_TOKEN_STORAGE_KEY),
        userName: "string",
        password: "string",
        info1: "string",
        info2: "string",
      },
      apiInfo: {
        clientId: "string",
        clientSecret: "string",
        key1: "string",
        key2: "string",
      },
      authFlow: 0,
    };
    return this.http.post<any>(url, data, options).pipe(
      catchError((errorResp) => {
        return throwError(errorResp);
      }),
      switchMap((resp) => {
        if (
          !!resp &&
          resp.code == "001" &&
          resp.result.organizationList.length > 0
        ) {
          let data = {
            clientCode: environment.engProClientCode, // "engagementpro-sslive", //environment.engProClientCode,
            module: "EngagementPro",
            authToken: resp.result.authToken,
            organizationToken: resp.result.organizationList[0],
          };

          this._simplestrataAuthService.engagementProData = data;
          return this.http
            .post<any>(
              `https://stemexess.azurewebsites.net/api/Stemexe/GetSSUserInfo`,
              data,
              options
            )
            .pipe(map((resp) => resp));
        } else {
          return of([]);
        }
      })
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class oppProTokenResolverService  {
  constructor(
    private _authService: AuthService,
    private http: HttpClient,
    private _simplestrataAuthService: SimplestrataAuthService,
    private simpleStrataDataProviderService: SimpleStrataDataProviderService
  ) {
    this.simpleStrataDataProviderService.initialize();
  }

  resolve() {
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.Idenedi),
    };
    let url = `${environment.idenediProviderUrl}/api/oauth/AuthorizationCode??client_id=XSS1977020300`;
    if (this._authService.idenediGroupId) {
      url = `${environment.idenediProviderUrl}/api/oauth/AuthorizationCode?client_id=XSS1977020300&gIdenedi=${this._authService.idenediGroupId}`;
    }
    return this.http.get<any>(url, options).pipe(
      catchError((errorResp) => {
        return throwError(errorResp);
      }),
      switchMap((code) => {
        let url = `${environment.oppProApi}/tenant/GetTenantUrlByIdenediAuthCode?ClientCode=${environment.oppProClientCode}&authorizationCode=${code}`;
        const options = {
          headers: this._authService.buildAuthHeader(AuthType.OppPro),
        };
        return this.http.get<any>(url, options).pipe(
          catchError((errorResp) => {
            // return [];
            return throwError(errorResp);
          }),
          switchMap((resp) => {
            if (!!resp && resp.length > 0) {
              let data = {
                customHeader: resp[0].customHeader,
                fclJson: resp[0].fclJson,
                idenediAccessToken: resp[0].idenediAccessToken,
                idenediRefreshToken: resp[0].idenediRefreshToken,
                tenantIsChild: resp[0].tenantIsChild,
                tenantIsMaster: resp[0].tenantIsMaster,
                tennantCode: resp[0].tennantCode,
                tennantMapUrl: resp[0].tennantMapUrl,
                tennantName: resp[0].tennantName,
                userIdentifier: resp[0].userIdentifier,
              };
              this._simplestrataAuthService.oppProData = {
                tennantMapUrl: data.tennantMapUrl,
                customHeader: data.customHeader,
              };
              environment.oppProApiHost = data.tennantMapUrl;
              let url = `${data.tennantMapUrl}/AuthenticateUserByTenant?idenedi=${data.userIdentifier}&accessToken=${data.idenediAccessToken}&refreshToken=${data.idenediRefreshToken}&Mode=web`;
              const options = {
                headers: new HttpHeaders({ customheader: data.customHeader }),
              };

              return this.http.post<any>(url, null, options).pipe(
                catchError((errorResp) => {
                  return throwError(errorResp);
                }),
                map((resp) => resp)
              );
            } else {
              return of([]);
            }
          })
        );
      })
    );
  }
}


@Injectable({
  providedIn: "root"
})
export class SimpleStrataDataProviderService implements IStemexeModule {
  type: AuthType = AuthType.SimpleStrata;
  constructor(
    private _activitiesService: ActivitiesService,
    private _authService: AuthService
  ) {
  }

  initialize(): void {
  }

  getDataProviders(providerType: string): Observable<DataProvider[]> {
    let espDataProviders: DataProvider[] = [];
    if (providerType == 'ActivitySource') {
      return this._authService.getAuthAddonByType(AuthType.SimpleStrata).pipe(
        switchMap(addon => {
          let reqDataProvider: DataProvider = new DataProvider();
          reqDataProvider.title = 'Score Card';
          reqDataProvider.id = '0';
          // reqDataProvider.system = simpleStrataAddon.BaseUrl;
          reqDataProvider.system = environment.simpleStrata.mainURL;
          reqDataProvider.tenantId = addon.OrganziationId; // orgId of SS

          reqDataProvider.getData = (token) => {
            return this._getProviderData(reqDataProvider, token)
          }
          espDataProviders.push(reqDataProvider);
          return of(espDataProviders);
        }));
    }
  }
  private _getProviderData(provider: DataProvider, nextPageToken?: any): Observable<PagedData> {
    if (provider.id == '0') {
      return this._activitiesService.SearchTeams(nextPageToken).pipe(map((resp) => {
        let pagedData = new PagedData();
        let items = resp.ResponseResult.map((obj) => {
          let item = new PagedDataItem();
          item.id = obj.Id;
          item.title = obj.Name;
          item.extraData = obj;
          item.getData = (nextPageToken) => this._getNextData(provider, item, nextPageToken);
          return item;
        });
        pagedData.items = items;
        return pagedData;
      }));
    } else {
      console.log('token', nextPageToken)
      let pagedData = new PagedData();
      return of(pagedData);
    }
  }


  private _getNextData(provider: DataProvider, pagedDataItem: PagedDataItem, nextPageToken?: any) {
    // let pagedData = new PagedData();
    return this._activitiesService.getAllByParent(nextPageToken).pipe(map((resp) => {
      let pagedData = new PagedData();
      let items = resp.ResponseResult.Tactics.map((obj) => {
        let item = new PagedDataItem();
        item.id = obj.TacticId;
        item.title = obj.TacticTitle;
        item.extraData = obj;
        return item;
      });
      pagedData.items = items;
      return pagedData;
    }));
  }


  openUrl(url: string, context: any): any { }
}

@Injectable({
  providedIn: "root"
})
export class AllDataProviderResolver  {
  constructor(
    private _ssDataProvider: SimpleStrataDataProviderService,
    private _planexeDataProvider: PlannexeDataProviderService,
    private _espDataProviderService: EspDataProviderService,
    private _engProDataProviderService: EngagementProDataProviderService,
    private _oppProDataProviderService: OpporunityProDataProviderService,
    private _technadoptDataProviderService: TechnadoptDataProviderService
  ) {
  }
  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    this._ssDataProvider.initialize();
    this._planexeDataProvider.initialize();
    this._espDataProviderService.initialize();
    this._engProDataProviderService.initialize();
    this._oppProDataProviderService.initialize();
    this._technadoptDataProviderService.initialize();
    return of(null);
  }
}
